/**
 * 权限守卫组件
 * 根据用户权限控制路由访问
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import { permissionService } from '../../../services/permissionService';

interface PermissionGuardProps {
  children: React.ReactNode;
  /**
   * 所需的菜单路径（用于权限检查）
   * 如果用户没有访问该路径的权限，将被拦截
   */
  requiredPath?: string;
  /**
   * 无权限时跳转的路径
   */
  fallbackPath?: string;
}

const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  children, 
  requiredPath,
  fallbackPath = '/data/home'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkPermission = async () => {
      // 如果还在加载认证状态，等待
      if (authLoading) {
        return;
      }

      // 如果未登录，跳转到登录页
      if (!isAuthenticated) {
        navigate('/login', { 
          state: { from: location.pathname },
          replace: true 
        });
        return;
      }

      // 如果没有指定requiredPath，直接允许访问（由其他组件控制）
      if (!requiredPath) {
        setHasPermission(true);
        setChecking(false);
        return;
      }

      try {
        // 优先从全局缓存获取菜单数据，避免重复调用接口
        let userMenus: any[] = [];
        let currentUserId: number | null = null;
        try {
          const userInfoStr = localStorage.getItem('user_info');
          if (userInfoStr) {
            const userInfo = JSON.parse(userInfoStr);
            currentUserId = userInfo?.id || null;
          }
        } catch (e) {
          // 忽略解析错误
        }
        
        const cacheKey: number | string | null = currentUserId;
        
        // 检查全局缓存
        if (cacheKey) {
          const globalMenuDataMap = (window as any).__globalMenuDataMap__;
          const globalIsLoadedMap = (window as any).__globalIsLoadedMap__;
          const globalIsLoading = (window as any).__globalIsLoading__;
          let loadingPromise = (window as any).__loadingPromise__;
          
          if (globalMenuDataMap && globalIsLoadedMap) {
            const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
            const cachedData = globalMenuDataMap.get(cacheKey);
            
            if (isLoaded && cachedData && cachedData.length > 0) {
              // 缓存中有数据，直接使用
              userMenus = cachedData;
            } else if (loadingPromise || globalIsLoading) {
              // 如果正在加载，等待完成（最多等待3秒）
              let retries = 0;
              while (retries < 30 && userMenus.length === 0) {
                // 重新检查 loadingPromise（因为它可能被更新）
                loadingPromise = (window as any).__loadingPromise__;
                
                if (loadingPromise) {
                  try {
                    await loadingPromise;
                    const updatedCachedData = globalMenuDataMap.get(cacheKey);
                    if (updatedCachedData && updatedCachedData.length > 0) {
                      userMenus = updatedCachedData;
                      break;
                    }
                  } catch (e) {
                    // 忽略错误，继续重试
                  }
                } else {
                  // loadingPromise 不存在，检查缓存是否已加载
                  const updatedIsLoaded = globalIsLoadedMap.get(cacheKey) || false;
                  const updatedCachedData = globalMenuDataMap.get(cacheKey);
                  if (updatedIsLoaded && updatedCachedData && updatedCachedData.length > 0) {
                    userMenus = updatedCachedData;
                    break;
                  }
                }
                
                // 等待一小段时间后重试
                await new Promise(resolve => setTimeout(resolve, 100));
                retries++;
              }
            }
          }
        }
        
        // 如果从缓存获取到数据，使用缓存数据
        // 如果缓存中没有数据且没有正在加载，作为备用方案调用 API
        // 注意：如果正在加载，不应该调用备用方案，应该继续等待
        if (userMenus.length === 0) {
          const globalIsLoading = (window as any).__globalIsLoading__;
          const loadingPromise = (window as any).__loadingPromise__;
          
          // 只有在确实没有加载且缓存中没有数据时才调用 API
          if (!globalIsLoading && !loadingPromise) {
            const response = await permissionService.getUserMenus();
            if (response.code === 200) {
              userMenus = response.data || [];
            }
          } else {
            // 如果正在加载，不应该调用备用方案
            // 默认允许访问，避免阻塞用户
            setHasPermission(true);
            setChecking(false);
            return;
          }
        }
        
        if (userMenus.length > 0) {
          // 检查用户是否有访问该路径的权限
          // 递归检查菜单树，看是否包含该路径
          const checkPathInMenus = (menus: any[], targetPath: string): boolean => {
            for (const menu of menus) {
              // 检查当前菜单路径（精确匹配）
              if (menu.path === targetPath || menu.component === targetPath) {
                return true;
              }
              
              // 检查路径前缀匹配（例如 /data/news 匹配 /data/news/edit）
              if (menu.path && targetPath.startsWith(menu.path + '/')) {
                return true;
              }
              if (menu.component && targetPath.startsWith(menu.component + '/')) {
                return true;
              }
              
              // 检查子菜单
              if (menu.children && menu.children.length > 0) {
                if (checkPathInMenus(menu.children, targetPath)) {
                  return true;
                }
              }
            }
            return false;
          };
          
          const hasAccess = checkPathInMenus(userMenus, requiredPath);
          
          if (!hasAccess) {
            // 没有权限，显示403页面（不自动跳转，让用户自己决定）
            setHasPermission(false);
          } else {
            setHasPermission(true);
          }
        } else {
          // 无法获取菜单数据，默认拒绝访问（显示403页面）
          setHasPermission(false);
        }
      } catch (error) {
        console.error('权限检查失败:', error);
        // 检查失败，默认拒绝访问（显示403页面）
        setHasPermission(false);
      } finally {
        setChecking(false);
      }
    };

    checkPermission();
  }, [isAuthenticated, authLoading, requiredPath, location.pathname, navigate, fallbackPath]);

  // 如果还在检查权限，显示加载状态
  if (checking || authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <div>正在验证权限...</div>
      </div>
    );
  }

  // 如果未登录，不渲染内容（已经在useEffect中跳转）
  if (!isAuthenticated) {
    return null;
  }

  // 如果没有权限，显示无权限页面（但保持布局结构）
  if (hasPermission === false) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        padding: '20px'
      }}>
        <Result
          status="403"
          title="403"
          subTitle="抱歉，您没有权限访问此页面。"
          extra={
            <Button type="primary" onClick={() => navigate(fallbackPath)}>
              返回首页
            </Button>
          }
        />
      </div>
    );
  }

  // 有权限，渲染子组件
  return <>{children}</>;
};

export default PermissionGuard;

